/**
 * Program Selection Form
 *
 * Form component for selecting an operational program (triage or CoCM)
 * during intake completion. Validates required fields and conditionally shows
 * service needs selection for referral-enabled programs.
 *
 * Inline-only (#986): every enrollment goes through a SteppedForm host, so the
 * component propagates validated values to the parent on change and renders no
 * submit/cancel buttons of its own. The former standalone mode was retired in
 * the 2026-09 cruft remediation.
 *
 * @agentic connections
 */

import { Stack, Textarea, Alert, LoadingOverlay } from "@mantine/core";
import {
  IconAlertCircle,
  IconCheck,
  IconClipboardList,
  IconClock,
  IconHeartHandshake,
} from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { useEffect, useRef } from "react";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { FormField } from "@benchmark/ui/components/atomic";
import { CoCMReferralToggle } from "@/components/domain/care-plan/CoCMReferralToggle";
import { ReferralServiceNeedsFields } from "@/components/domain/care-plan/ReferralServiceNeedsFields";
import { OPERATIONAL_PROGRAM_SELECT_OPTIONS } from "@benchmark/core/constants";
import {
  createEmptyServiceNeedFormItem,
  type ProgramSelectionFormData,
} from "@benchmark/core/types";
import type { EnhancedSelectOption } from "@/types/ui/component-props";
import styles from "./ProgramSelectionForm.module.css";

/**
 * Props for the ProgramSelectionForm.
 * @agentic connections
 */
export type ProgramSelectionFormProps = {
  /** Called with validated form data, or `undefined` when validation fails. */
  onSubmit: (data: ProgramSelectionFormData | undefined) => void;
  submitting?: boolean;
  error?: string | null;
  /** Pre-populated form data (when editing an existing selection) */
  initialValues?: Partial<ProgramSelectionFormData>;
  /** Whether the gate is already satisfied */
  gateSatisfied?: boolean;
  /** Whether to render Enroll Later as a card option. */
  allowEnrollLater?: boolean;
  /** Whether to render referrals details fields in this form. */
  showReferralFields?: boolean;
};

const PROGRAM_CARD_OPTIONS: EnhancedSelectOption[] = (
  OPERATIONAL_PROGRAM_SELECT_OPTIONS ?? []
).map((opt) => {
  const icons: Record<string, React.ReactNode> = {
    triage: <IconClipboardList size={20} />,
    cocm: <IconHeartHandshake size={20} />,
  };
  return {
    ...opt,
    icon: icons[opt.value],
  };
});

const ENROLL_LATER_OPTION_VALUE = "enroll-later";

const ENROLL_LATER_OPTION: EnhancedSelectOption = {
  value: ENROLL_LATER_OPTION_VALUE,
  label: "Enroll Later",
  description:
    "Skip enrollment for now and complete enrollment later from the task list.",
  icon: <IconClock size={20} />,
};

/**
 * Form for selecting an operational program during intake completion.
 * @agentic connections
 */
export const ProgramSelectionForm = ({
  onSubmit,
  submitting = false,
  error,
  initialValues,
  gateSatisfied = false,
  allowEnrollLater = true,
  showReferralFields = false,
}: ProgramSelectionFormProps) => {
  const cardOptions = allowEnrollLater
    ? [...PROGRAM_CARD_OPTIONS, ENROLL_LATER_OPTION]
    : [...PROGRAM_CARD_OPTIONS];

  const form = useForm<ProgramSelectionFormData>({
    initialValues: {
      selectedProgram: initialValues?.selectedProgram ?? null,
      enrollmentDecision:
        initialValues?.enrollmentDecision ??
        (initialValues?.selectedProgram ? "enroll-now" : undefined),
      reasonCodes: initialValues?.reasonCodes ?? [],
      reasonText: initialValues?.reasonText ?? "",
      selectedNeeds: initialValues?.selectedNeeds ?? [],
      serviceNeeds: initialValues?.serviceNeeds ?? [],
      includeReferrals: initialValues?.includeReferrals ?? false,
      requiresApproval: initialValues?.requiresApproval ?? true,
    },
    validate: {
      selectedProgram: (value, values) =>
        allowEnrollLater && values.enrollmentDecision === "enroll-later"
          ? null
          : value
            ? null
            : allowEnrollLater
              ? "Please select a care plan or choose Enroll Later"
              : "Please select a care plan",
      serviceNeeds: (value, values) => {
        const enrollmentActive =
          values.enrollmentDecision !== "enroll-later" &&
          !!values.selectedProgram;
        if (!showReferralFields || !enrollmentActive) {
          return null;
        }

        const referralsEnabled =
          values.selectedProgram === "triage" ||
          (values.selectedProgram === "cocm" &&
            values.includeReferrals === true);

        if (!referralsEnabled) {
          return null;
        }

        const serviceNeeds = value ?? [];
        const requiresAtLeastOneNeed =
          values.selectedProgram === "triage" ||
          (values.selectedProgram === "cocm" &&
            values.includeReferrals === true);
        if (requiresAtLeastOneNeed && serviceNeeds.length === 0) {
          return "At least one service need is required when referrals are included";
        }

        if (serviceNeeds.length > 0) {
          const missingType = serviceNeeds.find((item) => !item.serviceType);
          if (missingType) {
            return "Each service need must have a service type selected";
          }

          const missingMode = serviceNeeds.find(
            (item) => item.modes.length === 0,
          );
          if (missingMode) {
            return "Each service need must have at least one mode selected";
          }
        }

        return null;
      },
    },
  });

  // Propagate form values to the parent SteppedForm host on every change.
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);
  const inlineInitRef = useRef(false);

  const programValue = form.values.selectedProgram;
  const enrollmentDecision = form.values.enrollmentDecision;
  const includeReferrals = form.values.includeReferrals;
  const reasonCodesKey = form.values.reasonCodes.join(",");
  const reasonTextValue = form.values.reasonText;
  const serviceNeedsKey = form.values.serviceNeeds
    .map((item) => `${item.id}:${item.serviceType}:${item.modes.join("|")}`)
    .join(",");

  const referralsEnabled =
    form.values.selectedProgram === "triage" ||
    (form.values.selectedProgram === "cocm" &&
      form.values.enrollmentDecision === "enroll-now" &&
      form.values.includeReferrals === true);

  useEffect(() => {
    if (!showReferralFields) return;
    if (!referralsEnabled) return;
    if (form.values.serviceNeeds.length > 0) return;

    form.setFieldValue("serviceNeeds", [createEmptyServiceNeedFormItem()]);
  }, [showReferralFields, referralsEnabled, form]);

  useEffect(() => {
    if (!inlineInitRef.current) {
      inlineInitRef.current = true;
      return;
    }
    if (enrollmentDecision === "enroll-later") {
      onSubmitRef.current({
        ...form.values,
        selectedProgram: null,
        enrollmentDecision: "enroll-later",
        includeReferrals: false,
      });
      return;
    }
    if (!programValue) {
      onSubmitRef.current(undefined);
      return;
    }
    const isValid = form.isValid();
    onSubmitRef.current(isValid ? { ...form.values } : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enrollmentDecision,
    programValue,
    includeReferrals,
    reasonCodesKey,
    reasonTextValue,
    serviceNeedsKey,
  ]);

  const handleSubmit = form.onSubmit((values) => {
    onSubmit({ ...values });
  });

  return (
    <form onSubmit={handleSubmit} className={styles.formContainer}>
      <LoadingOverlay visible={submitting} />
      <Stack gap="lg">
        {gateSatisfied && (
          <Alert icon={<IconCheck size={16} />} color="green" variant="light">
            Program selection is complete. You may update it if needed.
          </Alert>
        )}

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            className={styles.gateError}
          >
            {error}
          </Alert>
        )}

        <FormField
          label="Select Care Plan"
          required
          variant="section"
          showDivider={false}
          description="Choose the operational care plan for this member"
        >
          <CardSelect
            value={
              form.values.enrollmentDecision === "enroll-later"
                ? ENROLL_LATER_OPTION_VALUE
                : (form.values.selectedProgram ?? "")
            }
            onChange={(value) => {
              const singleValue = Array.isArray(value) ? value[0] : value;
              if (singleValue === ENROLL_LATER_OPTION_VALUE) {
                form.setFieldValue("enrollmentDecision", "enroll-later");
                form.setFieldValue("selectedProgram", null);
                form.setFieldValue("includeReferrals", false);
                form.setFieldValue("serviceNeeds", []);
                form.setFieldValue("requiresApproval", true);
                return;
              }
              form.setFieldValue(
                "enrollmentDecision",
                singleValue ? "enroll-now" : undefined,
              );
              form.setFieldValue(
                "selectedProgram",
                (singleValue as ProgramSelectionFormData["selectedProgram"]) ||
                  null,
              );
              if (singleValue !== "cocm") {
                form.setFieldValue("includeReferrals", false);
              }
            }}
            data={cardOptions}
            columns={cardOptions.length}
            error={!!form.errors.selectedProgram}
          />
        </FormField>

        {form.values.selectedProgram === "cocm" &&
          form.values.enrollmentDecision === "enroll-now" && (
            <CoCMReferralToggle
              checked={!!form.values.includeReferrals}
              onChange={(checked) =>
                form.setFieldValue("includeReferrals", checked)
              }
            />
          )}

        {showReferralFields && referralsEnabled && (
          <ReferralServiceNeedsFields
            serviceNeeds={form.values.serviceNeeds}
            requiresApproval={form.values.requiresApproval ?? true}
            serviceNeedsError={
              form.errors.serviceNeeds ? String(form.errors.serviceNeeds) : null
            }
            onServiceNeedsChange={(items) =>
              form.setFieldValue("serviceNeeds", items)
            }
            onRequiresApprovalChange={(checked) =>
              form.setFieldValue("requiresApproval", checked)
            }
          />
        )}

        <FormField
          label="Selection Reasoning"
          variant="section"
          showDivider={false}
          description="Optional notes about this program selection"
        >
          <Textarea
            placeholder="Enter any additional context for this decision..."
            autosize
            minRows={2}
            maxRows={4}
            {...form.getInputProps("reasonText")}
          />
        </FormField>
      </Stack>
    </form>
  );
};
